import axios from 'axios';
import { log } from 'logrocket';
import { ReactNode, useEffect, useState } from 'react';
import { createMessageListener, sendMessage } from 'src/react/_sw/Messenger';
import { makeRandomString } from 'src/util/makeRandomString';
import { OnlineStatusContext } from './context';

const healthUrl = 'https://media.yembo.ai/online';
const NOTIFICATION_INTERVAL_DELAY = 1 * 60 * 60 * 1000; // 1 hour

export const OFFLINE_NOTIFICATION_MESSAGE =
  'You have unsaved images. Please connect to the internet to upload the images, otherwise your images may be lost';

const checkLineStatus = async () =>
  await axios.head(`${healthUrl}?${makeRandomString({ length: 32 })}=${makeRandomString({ length: 32 })}`, {
    timeout: 10 * 1000,
  });

export const OnlineStatusProvider = ({ children }: { children: ReactNode }) => {
  const [isOnline, setIsOnline] = useState(true);
  const [requestQueueLength, setRequestQueueLength] = useState(0);
  const [requestQueueLengthStart, setRequestQueueLengthStart] = useState(0);
  const [cachedImagesQueueLength, setCachedImagesQueueLength] = useState(0);
  const [shouldShowNotificationOnPage, setShouldShowNotificationOnPage] = useState(false);

  const areUnUploadedImagesExists = !isOnline && Boolean(cachedImagesQueueLength);

  const manualHandleLineStatus = async () => {
    try {
      await checkLineStatus();
      setIsOnline(true);
      return true;
    } catch {
      return false;
    }
  };

  // Force online check on line change
  useEffect(() => {
    const handleLineChange = async () => {
      try {
        await checkLineStatus();
        setIsOnline(true);
      } catch {
        setIsOnline(false);
      }
    };

    window.addEventListener('online', handleLineChange);
    window.addEventListener('offline', handleLineChange);

    return () => {
      window.removeEventListener('online', handleLineChange);
      window.removeEventListener('offline', handleLineChange);
    };
  }, []);

  useEffect(() => {
    sendMessage('notify_sw_api_status_change', isOnline);

    log(`Client went ${isOnline ? 'online' : 'offline'} @ ${new Date().toISOString()}`);
  }, [isOnline]);

  useEffect(() => {
    let failCounter = 0;
    let successCounter = 0;

    const handleStatus = async () => {
      try {
        await checkLineStatus();
        successCounter++;

        if (successCounter >= 6) setIsOnline(true);

        failCounter = 0;
      } catch {
        failCounter++;
        successCounter = 0;

        if (failCounter >= 2) setIsOnline(false);
      }
    };

    handleStatus();

    const timerId = setInterval(handleStatus, 10 * 1000);

    return () => clearInterval(timerId);
  }, []);

  useEffect(() => {
    if (isOnline) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShouldShowNotificationOnPage(false);
      return;
    }

    const intervalId = setInterval(() => {
      setShouldShowNotificationOnPage(areUnUploadedImagesExists);
    }, NOTIFICATION_INTERVAL_DELAY);

    return () => {
      clearInterval(intervalId);
    };
  }, [isOnline, areUnUploadedImagesExists]);

  useEffect(() => createMessageListener('log', (data: unknown) => log('sw_log', data)), []);

  useEffect(() => {
    return createMessageListener('notify_client_is_sw_ready', () => {
      sendMessage('notify_sw_api_status_change', isOnline);
    });
  }, [isOnline]);

  useEffect(() => {
    return createMessageListener('cached_images_queue_length', (length: number) => {
      setCachedImagesQueueLength(length);
      log('cached_images_queue_length', length);
    });
  }, []);

  useEffect(
    () => createMessageListener('requests_queue_length_start', (length: number) => setRequestQueueLengthStart(length)),
    [requestQueueLengthStart]
  );

  useEffect(
    () => createMessageListener('requests_queue_length', (length: number) => setRequestQueueLength(length)),
    [requestQueueLength]
  );

  useEffect(
    () => createMessageListener('notify_client_api_status_change', (isOnline: boolean) => setIsOnline(isOnline)),
    []
  );

  return (
    <OnlineStatusContext.Provider
      value={{
        isOnline,
        requestQueueLength,
        requestQueueLengthStart,
        shouldShowNotificationOnPage,
        manualHandleLineStatus,
      }}
    >
      {children}
    </OnlineStatusContext.Provider>
  );
};
